import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./hooks/useAuth";

// Helper component to redirect processing route to review with proper ID
const ProcessingRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/admin/batch-extractions/${id}/review`} replace />;
};

// Back-compat redirect for legacy /scholarships/* paths → /rosters/*.
const ScholarshipsRedirect = () => {
  const { '*': rest } = useParams<{ '*': string }>();
  return <Navigate to={`/rosters/${rest ?? ''}`} replace />;
};
import { LoginRoute } from "./features/login/LoginRoute";
import { ForgotPassword } from "./features/auth/ForgotPassword";
import { AcceptInvite } from "./features/auth/AcceptInvite";
import { SetupProgram } from "./features/auth/SetupProgram";
import { SignupPage } from "./features/signup/SignupPage";
import { SignupPaymentPage } from "./features/signup/SignupPaymentPage";
import { SignupCompletePage } from "./features/signup/SignupCompletePage";
import { SignupSessionRoute } from "./features/signup/SignupSessionRoute";
import { AuthActionPage } from "./features/auth/AuthActionPage";
import { DashboardScreen } from "./features/dashboard/DashboardScreen";
import { PlayersSection } from "./features/dashboard/sections/players/PlayersSection";
import { DuplicatesSection } from "./features/dashboard/sections/duplicates/DuplicatesSection";
import { TrustedContactsSection } from "./features/trusted-contacts/TrustedContactsSection";
import { CallsListSection } from "./features/dashboard/sections/calls/CallsListSection";
import { ScheduleSection } from "./features/dashboard/sections/schedule/ScheduleSection";
import { BuildItineraryWizard } from "./features/dashboard/sections/schedule/build-itinerary/BuildItineraryWizard";
import { VideosSection } from "./features/dashboard/sections/videos/VideosSection";
import { LatestNotesSection } from "./features/dashboard/sections/notes/LatestNotesSection";
import { CreateRecruitingNotePage } from "./features/dashboard/sections/notes/CreateRecruitingNotePage";
import { ScholarshipsSection } from "./features/dashboard/sections/scholarships/ScholarshipsSection";
import { RosterImageSection } from "./features/dashboard/sections/rosters/RosterImageSection";
import { ScholarshipDetailLayout } from "./features/dashboard/sections/scholarships/ScholarshipDetailLayout";
import { ScholarshipPlayersTab } from "./features/dashboard/sections/scholarships/ScholarshipPlayersTab";
import { ScholarshipDepthChartTab } from "./features/dashboard/sections/scholarships/ScholarshipDepthChartTab";
import { ScholarshipPitchingStaffTab } from "./features/dashboard/sections/scholarships/ScholarshipPitchingStaffTab";
import { ScholarshipLineupsTab } from "./features/dashboard/sections/scholarships/ScholarshipLineupsTab";
import { AdminLayout } from "./features/admin/AdminLayout";
import { AdminSection } from "./features/admin/AdminSection";
import { OrganizationDetail } from "./features/admin/OrganizationDetail";
import { NotificationsSection } from "./features/admin/NotificationsSection";
import { MessagingNumbersSection } from "./features/admin/MessagingNumbersSection";
import { AdminItineraryRequestsSection } from "./features/admin/AdminItineraryRequestsSection";
import { SampleDataSection } from "./features/admin/sample-data/SampleDataSection";
import { ToursSection } from "./features/admin/tours/ToursSection";
import { TourEditor } from "./features/admin/tours/TourEditor";
import { BatchExtractionsSection } from "./features/admin/batch-extractions/BatchExtractionsSection";
import { RosterUploadsSection } from "./features/admin/roster-uploads/RosterUploadsSection";
import { RosterUploadDetail } from "./features/admin/roster-uploads/RosterUploadDetail";
import { BatchExtractionWizard } from "./features/admin/batch-extractions/BatchExtractionWizard";
import { BatchExtractionReview } from "./features/admin/batch-extractions/BatchExtractionReview";
import { ScrapingSection } from "./features/admin/scraping/ScrapingSection";
import { BillingSection } from "./features/admin/billing/BillingSection";
import { BillingOrganizationDetail } from "./features/admin/billing/BillingOrganizationDetail";
import { BillingSettingsSection } from "./features/admin/billing/BillingSettingsSection";
import { OrgSettingsLayout } from "./features/settings/OrgSettingsLayout";
import { UsersSection } from "./features/settings/UsersSection";
import { MyBillingPage } from "./features/billing/MyBillingPage";
import { UserDetailPage } from "./features/settings/UserDetailPage";
import { LegalPage } from "./features/legal/LegalPage";
import {
  PRIVACY_ALIAS_PATH,
  PRIVACY_PATH,
  TERMS_ALIAS_PATH,
  TERMS_PATH,
} from "./features/legal/legalRoutes";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PERMISSIONS } from "./utils/permissions";
import { PlayerSubmissionForm } from "./features/public/PlayerSubmissionForm";
import { CrmLayout } from "./features/crm/CrmLayout";
import { CommunicationsPage } from "./features/crm/communications/CommunicationsPage";
import { ConnectorSettingsPage } from "./features/crm/connectors/ConnectorSettingsPage";
import { XPostTemplateAdminPage } from "./features/crm/settings/XPostTemplateAdminPage";
import { MessagingLayout } from "./features/messaging/MessagingLayout";
import { MessagingEnabledRoute } from "./features/messaging/MessagingEnabledRoute";
import { MessagingInboxPage } from "./features/messaging/MessagingInboxPage";
import { MessageTemplatesPage } from "./features/messaging/MessageTemplatesPage";
import { PhoneNumbersPage } from "./features/messaging/PhoneNumbersPage";
import { UnmatchedQueuePage } from "./features/crm/messages/UnmatchedQueuePage";
import { FollowersTabPage } from "./features/crm/twitter/FollowersTabPage";
import { PendingSubmissionsPage } from "./features/crm/submissions/PendingSubmissionsPage";
import { CrmImportPage } from "./features/crm/import/CrmImportPage";
import { ContactListPage } from "./features/crm/contacts/ContactListPage";
import { ContactDetailView } from "./features/crm/contacts/ContactDetailView";
import { TeamsListPage } from "./features/crm/teams/TeamsListPage";
import { CrmTeamDetailView } from "./features/crm/teams/CrmTeamDetailView";
import { RosterPilotLogo } from "./assets/RosterPilotLogo";
import { SchedulePrintPage } from "./features/dashboard/sections/schedule/print/SchedulePrintPage";
import { RouteTitle } from "./components/RouteTitle";
import "./App.css";
import { TourProvider } from "./features/tours/TourProvider";

const AppRoutes = () => {
  const { user, initializing } = useAuth();

  if (initializing) {
    return (
      <div className="app-loading">
        <div className="app-loading__logo">
          <RosterPilotLogo />
        </div>
        <div className="app-loading__spinner"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      {/*
        The printable schedule sits outside the dashboard layout on purpose: the page IS the
        printout, so no nav, header or banners. Same Page Access key as /schedule.
      */}
      <Route
        path="/schedule/print"
        element={
          <ProtectedRoute requiredPermission={PERMISSIONS.schedule}>
            <SchedulePrintPage />
          </ProtectedRoute>
        }
      />
      {/*
        Terms of Service and Privacy Policy, rendered from the marketing site's own pages. Public, and
        must never sit inside ProtectedRoute: they are linked from outside the app. The two longer
        names redirect so either spelling of the link works.
      */}
      <Route path={TERMS_PATH} element={<LegalPage kind="terms" />} />
      <Route path={PRIVACY_PATH} element={<LegalPage kind="privacy" />} />
      <Route path={TERMS_ALIAS_PATH} element={<Navigate to={TERMS_PATH} replace />} />
      <Route path={PRIVACY_ALIAS_PATH} element={<Navigate to={PRIVACY_PATH} replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      {/*
        Firebase's custom email action URL is one of these two, shared by every template (password
        reset and email verification alike), so both switch on `mode`. /reset-password keeps its
        reset flow for any other mode. Public, and must stay outside ProtectedRoute - the coach
        verifying an address has no RosterPilot user yet.
      */}
      <Route path="/reset-password" element={<AuthActionPage resetByDefault />} />
      <Route path="/auth/complete-reset" element={<AuthActionPage />} />
      {/*
        Public on purpose: the person accepting an invitation has no account yet, which is the whole
        point. It must never sit inside ProtectedRoute.
      */}
      <Route path="/accept-invite" element={<AcceptInvite />} />
      {/*
        A platform admin's setup invitation: the invitee creates a new program and becomes its admin.
        Public for the same reason as /accept-invite, and must never sit inside ProtectedRoute.
      */}
      <Route path="/setup-program" element={<SetupProgram />} />
      {/*
        Self-serve signup. /signup is public for the same reason as /accept-invite. The two after it
        need a session but must NOT sit inside ProtectedRoute, which is what sends an organization
        awaiting payment to them: /signup/payment is also Stripe's cancel URL and /signup/complete
        its success URL.
      */}
      <Route path="/signup" element={<SignupPage />} />
      <Route
        path="/signup/payment"
        element={
          <SignupSessionRoute>
            <SignupPaymentPage />
          </SignupSessionRoute>
        }
      />
      <Route
        path="/signup/complete"
        element={
          <SignupSessionRoute>
            <SignupCompletePage />
          </SignupSessionRoute>
        }
      />
      <Route path="/submit/:orgToken" element={<PlayerSubmissionForm />} />

      {/* Main application routes - shared layout */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardScreen />
          </ProtectedRoute>
        }
      >
        <Route path="/players" element={<PlayersSection />} />
        <Route
          path="/trusted-contacts"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.trustedContacts} explainDenial>
              <TrustedContactsSection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/duplicates"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.duplicates}>
              <DuplicatesSection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calls"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.calls}>
              <CallsListSection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/schedule"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.schedule}>
              <ScheduleSection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/schedule/build-itinerary"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.schedule}>
              <BuildItineraryWizard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/video"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.video}>
              <VideosSection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/latest-notes"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.notes}>
              <LatestNotesSection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruiting-notes/new"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.notes}>
              <CreateRecruitingNotePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rosters"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.rosters}>
              <ScholarshipsSection />
            </ProtectedRoute>
          }
        />
        {/* Static segment must precede the `:id` route so it isn't captured as a scholarship id. */}
        <Route
          path="/rosters/roster-images"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.rosters}>
              <RosterImageSection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rosters/:id"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.rosters}>
              <ScholarshipDetailLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="players" replace />} />
          <Route path="players" element={<ScholarshipPlayersTab />} />
          <Route path="depth-chart" element={<ScholarshipDepthChartTab />} />
          <Route path="pitching-staff" element={<ScholarshipPitchingStaffTab />} />
          <Route path="lineups" element={<ScholarshipLineupsTab />} />
          <Route path="*" element={<Navigate to="players" replace />} />
        </Route>
        {/* Back-compat: redirect old /scholarships paths to /rosters. */}
        <Route path="/scholarships" element={<Navigate to="/rosters" replace />} />
        <Route path="/scholarships/*" element={<ScholarshipsRedirect />} />
      </Route>

      {/* System Admin routes - nested under AdminLayout */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["system_admin"]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminSection />} />
        <Route path="notifications" element={<NotificationsSection />} />
        <Route path="organizations/:id" element={<OrganizationDetail />} />
        <Route path="roster-uploads" element={<RosterUploadsSection />} />
        <Route path="roster-uploads/:id" element={<RosterUploadDetail />} />
        <Route path="batch-extractions" element={<BatchExtractionsSection />} />
        <Route path="batch-extractions/new" element={<BatchExtractionWizard />} />
        {/* Redirect processing to review for backward compatibility */}
        <Route path="batch-extractions/:id/processing" element={<ProcessingRedirect />} />
        <Route path="batch-extractions/:id/review" element={<BatchExtractionReview />} />
        <Route path="scraping" element={<ScrapingSection />} />
        <Route path="messaging-numbers" element={<MessagingNumbersSection />} />
        <Route path="itinerary-requests" element={<AdminItineraryRequestsSection />} />
        <Route path="sample-data" element={<SampleDataSection />} />
        <Route path="billing" element={<BillingSection />} />
        {/* Settings before the :orgId detail so "settings" is never read as an organization id. */}
        <Route path="billing/settings" element={<BillingSettingsSection />} />
        <Route path="billing/organizations/:orgId" element={<BillingOrganizationDetail />} />
        <Route path="tours" element={<ToursSection />} />
        <Route path="tours/:tourKey" element={<TourEditor />} />
      </Route>

      {/* Organization Settings route - for org admins and system admins */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={["admin", "system_admin"]}>
            <OrgSettingsLayout />
          </ProtectedRoute>
        }
      >
        {/*
          No settings landing page: it was a card that only linked to Users, and the left nav now
          expands to the destinations directly. /settings stays valid so old links still work.
        */}
        <Route index element={<Navigate to="/settings/users" replace />} />
        <Route path="users" element={<UsersSection />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        {/*
          The organization's own bill. Under /settings because it is already role-gated to admin
          and system_admin there, which is exactly who should see it - a new top-level destination
          would need its own Page Access key to withhold from a coach, and this needs no such
          thing since a coach cannot reach /settings at all.
        */}
        <Route path="billing" element={<MyBillingPage />} />
      </Route>

      {/* CRM Communication routes - system admins only; the API matches */}
      <Route
        path="/crm"
        element={
          <ProtectedRoute allowedRoles={["system_admin"]}>
            <CrmLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/crm/contacts" replace />} />
        <Route path="contacts" element={<ContactListPage />} />
        <Route path="contacts/:id" element={<ContactDetailView />} />
        <Route path="import" element={<CrmImportPage />} />
        <Route path="communications" element={<CommunicationsPage />} />
        <Route path="messages/unmatched" element={<UnmatchedQueuePage />} />
        <Route path="twitter/followers" element={<FollowersTabPage />} />
        <Route path="submissions/pending" element={<PendingSubmissionsPage />} />
        <Route path="teams" element={<TeamsListPage />} />
        <Route path="teams/:id" element={<CrmTeamDetailView />} />
        <Route path="connectors" element={<ConnectorSettingsPage />} />
        <Route path="settings/x-post-templates" element={<XPostTemplateAdminPage />} />
      </Route>

      {/* Messaging — a destination of its own, not a CRM section. Open to every coach, because a
          coach texting a recruit is the feature; only number administration is admin-gated. */}
      <Route
        path="/messaging"
        element={
          <ProtectedRoute requiredPermission={PERMISSIONS.messaging}>
            <MessagingEnabledRoute>
              <MessagingLayout />
            </MessagingEnabledRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<MessagingInboxPage />} />
        <Route path="templates" element={<MessageTemplatesPage />} />
        <Route
          path="numbers"
          element={
            <ProtectedRoute allowedRoles={["admin", "system_admin"]}>
              <PhoneNumbersPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/messaging" replace />} />
      </Route>

      <Route
        path="/"
        element={<Navigate to={user ? "/players" : "/login"} replace />}
      />

      {/* Catch-all route for non-existent paths */}
      <Route
        path="*"
        element={<Navigate to={user ? "/players" : "/login"} replace />}
      />
    </Routes>
  );
};

export const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/*
         * A sibling of the routes, not a wrapper: sibling effects run in render
         * order, so a page that titles itself from loaded data still wins over
         * the static map here.
         */}
        <RouteTitle />
        <TourProvider>
          <AppRoutes />
        </TourProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};
